import {Component, OnDestroy, OnInit} from '@angular/core';
import {Observable, Subscription} from 'rxjs';
import {MemoizedSelector, select, Store} from '@ngrx/store';
import {correlationIdSelector} from '../../../shared/store/samoobsluga/samoobsluga.selectors';
import {czyPosiadaNumerKontaSelector} from '../../../shared/store/samoobsluga/daneKlienta/daneKlienta.selectors';
import {
  AbstractControl,
  FormControl,
  ValidationErrors,
  ValidatorFn
} from '@angular/forms';
import {kliknietoZaakceptuj} from './store/ekran-decyzji.actions';
import {NavigationService} from '../../../shared/services/navigation/navigation.service';
import {TenantService} from '../../../shared/services/tenant/tenant.service';
import {
  parametryKwotyDocelowejSelector
} from '../../../shared/store/samoobsluga/parametryKwotyDocelowej/parametryKwotyDocelowej.selectors';
import {validateIBAN} from 'ibantools';
import {czyIsteniejeUszkodzenieONazwie} from "../../../shared/store/zbior-uszkodzen/zbiorUszkodzen.selectors";
import {SlownikUszkodzen} from "../../../shared/model/uszkodzenia/slownik-uszkodzen";
import {take} from "rxjs/operators";
import {selectIdPrzedmiotowWZbiorze} from "../../../shared/store/zbior-uszkodzen-przedmioty/zbiorUszkodzenPrzedmiotow.selectors";
import {czyPrzedmiotTypu} from "../../../shared/store/samoobsluga/przedmioty/przedmioty-selectors";
import {TypPrzedmiotuUbezpieczenia} from "../../../shared/model/przedmioty/typPrzedmiotuUbezpieczenia";

@Component({
  selector: 'app-ekran-decyzji',
  templateUrl: './ekran-decyzji.component.html',
  styleUrls: ['./ekran-decyzji.component.scss']
})
export class EkranDecyzjiComponent implements OnInit, OnDestroy {

  constructor(private store: Store, private navigationService: NavigationService, public tenantService: TenantService) {
  }

  kwota!: number;
  czyKlientPosiadaNumerKonta!: Observable<boolean>;
  numerKontaWprowadzony!: FormControl<string | null>;
  correlationId!: string;
  correlationIdSubscribe!: Subscription;
  pokazKomunikat = false;

  ngOnInit(): void {
    this.store.pipe(select(parametryKwotyDocelowejSelector)).subscribe(parametryKwotyDocelowej => {
      if (parametryKwotyDocelowej?.kwotaDocelowa) {
        this.kwota = parametryKwotyDocelowej.kwotaDocelowa;
      }
    });

    this.czyKlientPosiadaNumerKonta = this.store.select(czyPosiadaNumerKontaSelector);

    this.numerKontaWprowadzony = new FormControl<string | null>('', [
      this.waliduj()
    ]);

    let poprzedniaWartosc = '';

    this.numerKontaWprowadzony.valueChanges.subscribe(value => {
      if (value === poprzedniaWartosc) return;

      if (value && !value.startsWith('PL')) {
        setTimeout(() => {
          const nowaWartosc = 'PL' + value;
          this.numerKontaWprowadzony.patchValue(nowaWartosc, {emitEvent: false});
          poprzedniaWartosc = nowaWartosc;
        });
      } else if (value === 'PL') {
        setTimeout(() => {
          this.numerKontaWprowadzony.patchValue('', {emitEvent: false});
          poprzedniaWartosc = '';
        });
      } else {
        poprzedniaWartosc = value ?? '';
      }
    });

    this.correlationIdSubscribe = this.store.pipe(select(correlationIdSelector)).subscribe(correlationID => {
      this.correlationId = correlationID;
    });
  }


  ngOnDestroy(): void {
    this.correlationIdSubscribe?.unsubscribe();
  }

  waliduj(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const rawValue = (control.value || '').trim();
      const iban = rawValue.startsWith('PL') ? rawValue : 'PL' + rawValue;

      return validateIBAN(iban).valid || !rawValue
        ? null
        : {blad: 'błędny numer konta'};
    };
  }

  zaakceptuj(): void {
    this.store.dispatch(kliknietoZaakceptuj({
      correlationId: this.correlationId,
      numerKonta: this.numerKontaWprowadzony.value ? this.numerKontaWprowadzony.value.replace(/\s/g, '') : '',
    }));
  }

  odrzuc(): void {
    this.navigationService.navigate(['decyzja/przyczynaOdrzucenia']);
  }

  public zmienKomunikat(flaga: boolean): void {
    this.pokazKomunikat = flaga;
  }

  naglowekUszkodzenieMebliSprzetuAgdRtv(): String {
    return this.typUszkodzeniaMeble() && this.typUszkodzeniaSprzetAgdRtv() ? "Meble i sprzęt AGD/RTV"
      : this.typUszkodzeniaMeble() ? "Meble"
        : "Sprzęt AGD/RTV";
  }

  komunikatUszkodzenieMebliSprzetuAgdRtv(): String {
    return this.typUszkodzeniaMeble() && this.typUszkodzeniaSprzetAgdRtv() ? "mebli i sprzętu AGD/RTV"
      : this.typUszkodzeniaMeble() ? "mebli"
        : "sprzętu AGD/RTV";
  }

  naglowekPrzedmiotuZgloszenia(): String {
    if (this.typPrzedmiotuPrzedmiotSzklany() || this.typPrzedmiotuOszkleniaWitraze()) {
      return "Przedmioty od stłuczenia";
    } else if (this.typPrzedmiotuBudowle()) {
      return "Budowle";
    } else {
      return "Nieruchomości";
    }
  }

  typUszkodzeniaMeble(): boolean {
    return this.select(czyIsteniejeUszkodzenieONazwie(SlownikUszkodzen.MEBLE))
  }

  typUszkodzeniaSprzetAgdRtv(): boolean {
    return this.select(czyIsteniejeUszkodzenieONazwie(SlownikUszkodzen.SPRZET_AGD_RTV))
  }

  private typPrzedmiotuPrzedmiotSzklany(): boolean {
    const idPrzedmiotowZeZbioru = this.select(selectIdPrzedmiotowWZbiorze)
    return this.select(czyPrzedmiotTypu(idPrzedmiotowZeZbioru, TypPrzedmiotuUbezpieczenia.PRZEDMIOTY_SZKLANE))
  }

  private typPrzedmiotuOszkleniaWitraze(): boolean {
    const idPrzedmiotowZeZbioru = this.select(selectIdPrzedmiotowWZbiorze)
    return this.select(czyPrzedmiotTypu(idPrzedmiotowZeZbioru, TypPrzedmiotuUbezpieczenia.OSZKLENIA_WITRAZE))
  }

  private typPrzedmiotuBudowle(): boolean {
    const idPrzedmiotowZeZbioru = this.select(selectIdPrzedmiotowWZbiorze)
    return this.select(czyPrzedmiotTypu(idPrzedmiotowZeZbioru, TypPrzedmiotuUbezpieczenia.BUDOWLE))
  }


  private select<T>(selector: MemoizedSelector<object, T>): T {
    let result: T | undefined = undefined;
    this.store.select(selector)
      .pipe(take(1)).subscribe(res => result = res)
    return result as T;
  }
}
